// Unified input: mouse (absolute aim + hold to charge), touch (drag to aim, tap to place,
// hold the on-screen button to charge) and keyboard. All listeners are removed by dispose().

export class Input {
  /**
   * handlers: { aimAt(clientX, clientY), aimBy(dxPx, dyPx), chargeStart(), chargeEnd(), chargeCancel(),
   *             curveStep(dir), pause(), mute(), confirm(), touchMode() }
   */
  constructor(canvas, shootButton, handlers) {
    this.canvas = canvas;
    this.shoot = shootButton;
    this.h = handlers;
    this.keys = new Set();
    this.touch = false;
    this.offs = [];
    this.touchStart = null;
    this.mouseCharging = false;
    this.buttonPointer = null;

    const on = (target, type, fn, opts) => { target.addEventListener(type, fn, opts); this.offs.push(() => target.removeEventListener(type, fn, opts)); };

    on(canvas, 'pointermove', (e) => {
      if (e.pointerType === 'mouse') { this.setTouch(false); this.h.aimAt(e.clientX, e.clientY); return; }
      if (this.touchStart && e.pointerId === this.touchStart.id) {
        const dx = e.clientX - this.touchStart.lastX;
        const dy = e.clientY - this.touchStart.lastY;
        this.touchStart.lastX = e.clientX;
        this.touchStart.lastY = e.clientY;
        this.touchStart.moved += Math.abs(dx) + Math.abs(dy);
        this.h.aimBy(dx, dy);
        e.preventDefault();
      }
    });
    on(canvas, 'pointerdown', (e) => {
      this.h.gesture?.();
      if (e.pointerType === 'mouse') {
        if (e.button !== 0) return;
        this.setTouch(false);
        this.h.aimAt(e.clientX, e.clientY);
        this.mouseCharging = true;
        canvas.setPointerCapture?.(e.pointerId);
        this.h.chargeStart();
      } else {
        this.setTouch(true);
        this.touchStart = { id: e.pointerId, x: e.clientX, y: e.clientY, lastX: e.clientX, lastY: e.clientY, moved: 0, t: performance.now() };
        e.preventDefault();
      }
    });
    const endCanvas = (e, cancelled) => {
      if (e.pointerType === 'mouse') {
        if (!this.mouseCharging) return;
        this.mouseCharging = false;
        if (cancelled) this.h.chargeCancel(); else this.h.chargeEnd();
      } else if (this.touchStart && e.pointerId === this.touchStart.id) {
        const quick = performance.now() - this.touchStart.t < 300 && this.touchStart.moved < 10;
        if (quick && !cancelled) this.h.aimAt(e.clientX, e.clientY - 40); // tap: place the target just above the finger
        this.touchStart = null;
      }
    };
    on(canvas, 'pointerup', (e) => endCanvas(e, false));
    on(canvas, 'pointercancel', (e) => endCanvas(e, true));
    on(canvas, 'contextmenu', (e) => e.preventDefault());

    // on-screen shoot button (touch, but also works with a mouse)
    on(shootButton, 'pointerdown', (e) => {
      this.h.gesture?.();
      if (e.pointerType !== 'mouse') this.setTouch(true);
      this.buttonPointer = e.pointerId;
      shootButton.setPointerCapture?.(e.pointerId);
      e.preventDefault();
      this.h.chargeStart();
    });
    const endButton = (e, cancelled) => {
      if (this.buttonPointer !== e.pointerId) return;
      this.buttonPointer = null;
      if (cancelled) this.h.chargeCancel(); else this.h.chargeEnd();
    };
    on(shootButton, 'pointerup', (e) => endButton(e, false));
    on(shootButton, 'pointercancel', (e) => endButton(e, true));
    on(shootButton, 'contextmenu', (e) => e.preventDefault());

    // keyboard
    on(window, 'keydown', (e) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) return;
      const k = e.key.toLowerCase();
      if (['arrowleft', 'arrowright', 'arrowup', 'arrowdown', ' '].includes(k)) e.preventDefault();
      if (e.repeat) return;
      this.keys.add(k);
      this.h.gesture?.();
      if (k === ' ') this.h.chargeStart();
      else if (k === 'q') this.h.curveStep(-1);
      else if (k === 'e') this.h.curveStep(1);
      else if (k === 'p' || k === 'escape') this.h.pause();
      else if (k === 'm') this.h.mute();
      else if (k === 'enter') this.h.confirm();
    });
    on(window, 'keyup', (e) => {
      const k = e.key.toLowerCase();
      this.keys.delete(k);
      if (k === ' ') this.h.chargeEnd();
    });
    // never leave a control stuck if the window loses focus mid-press
    on(window, 'blur', () => this.releaseAll());
    on(document, 'visibilitychange', () => { if (document.hidden) this.releaseAll(); });

    if (matchMedia('(pointer: coarse)').matches) this.setTouch(true);
  }

  releaseAll() {
    const wasCharging = this.mouseCharging || this.buttonPointer !== null || this.keys.has(' ');
    this.keys.clear();
    this.mouseCharging = false;
    this.buttonPointer = null;
    this.touchStart = null;
    if (wasCharging) this.h.chargeCancel();
  }

  setTouch(t) {
    if (this.touch === t) return;
    this.touch = t;
    document.body.classList.toggle('touch', t);
    this.h.touchMode?.(t);
  }

  /** Continuous keyboard aim direction for this frame: [x, y] in -1..1. */
  keyAim() {
    const k = this.keys;
    const x = (k.has('arrowright') || k.has('d') ? 1 : 0) - (k.has('arrowleft') || k.has('a') ? 1 : 0);
    const y = (k.has('arrowup') || k.has('w') ? 1 : 0) - (k.has('arrowdown') || k.has('s') ? 1 : 0);
    return [x, y];
  }

  dispose() { for (const off of this.offs) off(); this.offs.length = 0; }
}
